import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Play, Plus, X, Loader2 } from 'lucide-react';
import { fetchGalleryItemsFiltered, fetchFeaturedGalleryItems, type GalleryItem } from '@/lib/cms';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const GRADIENT_PALETTES = [
  'from-[#635bff] to-[#00d4ff]',
  'from-[#00d4ff] to-[#00ff88]',
  'from-[#a855f7] to-[#ec4899]',
  'from-[#ff6b6b] to-[#ffa500]',
  'from-[#635bff] to-[#a855f7]',
  'from-[#00d4ff] to-[#635bff]',
];

const PAGE_SIZE = 60;

// ---------------------------------------------------------------------------
// Filter options — mirror the gallery collection enums in the CMS.
// With server-side filtering the grid only returns matching rows, so option
// lists can no longer be derived from the loaded data; they use the known
// enums instead. Keep in sync with src/collections/gallery.collection.ts.
// ---------------------------------------------------------------------------
type Option = { id: string; label: string };
const toOpts = (allLabel: string, values: string[]): Option[] => [
  { id: 'all', label: allLabel },
  ...values.map(v => ({ id: v, label: v })),
];

const FORMAT_OPTIONS = toOpts('All Formats', [
  '3D Banner', 'Adhesion', 'Banner', 'Cinema', 'Cube', 'Eclipse', 'Expandable',
  'Full Page Flex', 'Interstitial', 'Interscroller', 'Marquee', 'Outstream Video',
  'Pushdown', 'Sticky Sidebar', 'Takeover', 'Wallpaper',
]);
const INDUSTRY_OPTIONS = toOpts('All Industries', [
  'Academic', 'Automotive', 'B2B Services', 'Bars & Restaurants', 'Beauty',
  'Consumer Packaged Goods', 'Culinary', 'Design', 'Entertainment', 'Fashion',
  'Finance', 'Gaming', 'Health & Wellness', 'Real Estate', 'Services', 'Shopping',
  'Technology', 'Travel & Tourism',
]);
const COMPONENT_OPTIONS = toOpts('All Components', [
  'Audio Player', 'Cartograph Map', 'Cartograph Slider', 'Dynamic Data', 'Forms',
  'Hotspots/Tooltips', 'HTML5 Embeddable Content', 'Image Compare', 'Parallax Carousel',
  'Screen Flip', 'Slideshow', 'Social Feeds', 'Tabs', 'Text & Image Scroller', 'Timer',
  'VR / 360 Video', 'Video Player',
]);

// ---------------------------------------------------------------------------
// Gallery Card
// ---------------------------------------------------------------------------
const FEATURED_COUNT = 3;

function GalleryCard({
  item,
  idx,
  isDark,
  featured = false,
  onImport,
  onViewDemo,
}: {
  item: GalleryItem;
  idx: number;
  isDark: boolean;
  featured?: boolean;
  onImport?: (item: GalleryItem) => void;
  onViewDemo?: (item: GalleryItem) => void;
}) {
  const showImport = item.allow_console_import && !!item.console_import_url;
  const maxTags = featured ? 3 : 2;

  return (
    <div
      className={`group border rounded-xl overflow-hidden hover:border-[#635bff]/40 hover:shadow-md transition-all cursor-pointer ${
        isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'
      }`}
      onClick={() => onViewDemo?.(item)}
    >
      {/* Thumbnail */}
      <div className={`relative ${featured ? 'aspect-[16/9]' : 'aspect-[4/3]'} overflow-hidden ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
        {item.featured_image ? (
          <img
            src={item.featured_image}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading={featured ? 'eager' : 'lazy'}
            decoding="async"
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${GRADIENT_PALETTES[idx % GRADIENT_PALETTES.length]} flex items-center justify-center p-4`}>
            <span className={`text-white/90 font-semibold text-center line-clamp-3 ${featured ? 'text-base' : 'text-sm'}`}>{item.title}</span>
          </div>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
          <div className={`flex gap-2 w-full ${featured ? 'flex-nowrap' : ''}`}>
            <button
              onClick={(e) => { e.stopPropagation(); onViewDemo?.(item); }}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 bg-[#635bff] text-white text-sm font-medium rounded-lg hover:bg-[#5449e6] transition-colors ${featured ? 'px-4' : 'px-3'}`}
            >
              <Play className="w-3.5 h-3.5 flex-shrink-0" />
              View Demo
            </button>
            {showImport && (
              <button
                onClick={(e) => { e.stopPropagation(); onImport?.(item); }}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-lg hover:bg-white/30 transition-colors ${featured ? 'px-4' : 'px-3'}`}
              >
                <Plus className="w-3.5 h-3.5 flex-shrink-0" />
                Add
              </button>
            )}
          </div>
        </div>

        {/* Featured badge */}
        {featured && (
          <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-medium text-white bg-[#635bff] rounded-full">
            Featured
          </span>
        )}

        {/* Format badge */}
        {item.ad_format && (
          <span className={`absolute ${featured ? 'top-2 right-2' : 'top-2 left-2'} px-2 py-0.5 text-[10px] font-medium text-white bg-black/50 backdrop-blur-sm rounded-full`}>
            {item.ad_format}
          </span>
        )}
      </div>

      {/* Card body */}
      <div className={featured ? 'p-4' : 'p-3'}>
        <h3 className={`font-semibold group-hover:text-[#635bff] transition-colors line-clamp-1 ${featured ? 'text-base' : 'text-sm'} ${isDark ? 'text-white' : 'text-slate-900'}`}>
          {item.title}
        </h3>
        {(item.excerpt || item.description) && (
          <p className={`mt-1 text-xs line-clamp-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            {item.excerpt || item.description}
          </p>
        )}
        {item.industry.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {item.industry.slice(0, maxTags).map(ind => (
              <span key={ind} className={`px-1.5 py-0.5 text-[10px] rounded ${isDark ? 'text-slate-400 bg-white/5' : 'text-slate-500 bg-slate-100'}`}>
                {ind}
              </span>
            ))}
            {item.industry.length > maxTags && (
              <span className={`px-1.5 py-0.5 text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                +{item.industry.length - maxTags}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// postMessage theme bridge — security helpers
// ---------------------------------------------------------------------------

const DEBUG = false;

function isAllowedParentOrigin(origin: string): boolean {
  let parsed: URL;
  try { parsed = new URL(origin); } catch { return false; }
  if (parsed.protocol !== 'https:') return false;
  const h = parsed.hostname;
  return (
    h === 'adventive.dev'  || h.endsWith('.adventive.dev') ||
    h === 'adventive.com'  || h.endsWith('.adventive.com')
  );
}

type ThemeMode = 'light' | 'dark';

function isValidMode(v: unknown): v is ThemeMode {
  return v === 'light' || v === 'dark';
}

/*
 * LOCAL TEST SNIPPET (browser console only — paste while iframe is open):
 *
 * const iframe = document.querySelector('iframe');
 * iframe.contentWindow.dispatchEvent(
 *   new MessageEvent('message', {
 *     data: { type: 'theme-change', mode: 'dark' },
 *     origin: window.location.origin,   // must match allowed origins in prod
 *     source: window,
 *   })
 * );
 */

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const InAppGallery: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // ?mode=light|dark — initial theme from URL param, overridable via postMessage
  const initialMode: ThemeMode = searchParams.get('mode') === 'dark' ? 'dark' : 'light';
  const [mode, setMode] = useState<ThemeMode>(initialMode);

  // Set <html> and <body> classes synchronously before first paint so the correct mode is
  // applied before ThemeProvider's useEffect can override, and before HubSpot's widget
  // initializes (which checks body/html for .dark to pick its own widget skin).
  useLayoutEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    html.classList.remove('light', 'dark');
    html.classList.add(mode);
    html.style.colorScheme = mode;
    body.classList.remove('light', 'dark');
    body.classList.add(mode);

    // HubSpot's widget container covers the full viewport with a white background by default.
    // In dark mode it normally detects the page theme and goes transparent, but that detection
    // fails on this embed page. Force it transparent so the dark page background shows through.
    let styleEl: HTMLStyleElement | null = null;
    if (mode === 'dark') {
      styleEl = document.createElement('style');
      styleEl.id = 'iag-hubspot-dark-fix';
      styleEl.textContent = '#hubspot-messages-iframe-container { background: transparent !important; background-color: transparent !important; }';
      document.head.appendChild(styleEl);
    }

    return () => {
      html.classList.remove('light', 'dark');
      html.style.colorScheme = '';
      body.classList.remove('light', 'dark');
      styleEl?.remove();
    };
  }, [mode]);

  // Listen for theme-change messages from trusted parent origins
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (!isAllowedParentOrigin(event.origin)) {
        if (DEBUG) console.debug('[InAppGallery] rejected origin:', event.origin);
        return;
      }
      const data = event.data;
      if (data === null || typeof data !== 'object') return;
      if (data.type !== 'theme-change') return;
      if (!isValidMode(data.mode)) return;
      if (DEBUG) console.debug('[InAppGallery] accepted theme-change:', data.mode);
      setMode(data.mode);
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const [items, setItems] = useState<GalleryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [featured, setFeatured] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') ?? '');
  const [selectedFormat, setSelectedFormat] = useState(() => searchParams.get('format') ?? 'all');
  const [selectedIndustry, setSelectedIndustry] = useState(() => searchParams.get('industry') ?? 'all');
  const [selectedComponent, setSelectedComponent] = useState(() => searchParams.get('component') ?? 'all');

  // Sync filter state back to URL (replace so iframe history stays clean).
  // Search is debounced 300ms so typing doesn't produce a URL write per keystroke.
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const apply = (q: string) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        q ? next.set('search', q) : next.delete('search');
        selectedFormat !== 'all' ? next.set('format', selectedFormat) : next.delete('format');
        selectedIndustry !== 'all' ? next.set('industry', selectedIndustry) : next.delete('industry');
        selectedComponent !== 'all' ? next.set('component', selectedComponent) : next.delete('component');
        return next;
      }, { replace: true });
    };
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => apply(searchQuery), 300);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchQuery, selectedFormat, selectedIndustry, selectedComponent]); // eslint-disable-line react-hooks/exhaustive-deps

  const sentinelRef = useRef<HTMLDivElement>(null);

  // Static option lists (server-side filtering means we can't derive them from data)
  const formatOptions = FORMAT_OPTIONS;
  const industryOptions = INDUSTRY_OPTIONS;
  const componentOptions = COMPONENT_OPTIONS;

  const hasActiveFilters = searchQuery !== '' || selectedFormat !== 'all' || selectedIndustry !== 'all' || selectedComponent !== 'all';

  // Debounce free-text search so typing doesn't fire a request per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Featured row — fetched once via server-side filter[featured][equals]=true.
  useEffect(() => {
    let cancelled = false;
    fetchFeaturedGalleryItems(FEATURED_COUNT)
      .then(f => { if (!cancelled) setFeatured(f); })
      .catch(() => { /* featured row is non-critical */ });
    return () => { cancelled = true; };
  }, []);

  // Server-side filtered fetch — refetches whenever a filter/search changes.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const { items: rows, total: t } = await fetchGalleryItemsFiltered({
          format: selectedFormat,
          industry: selectedIndustry,
          component: selectedComponent,
          search: debouncedSearch || undefined,
          limit: PAGE_SIZE,
          offset: 0,
        });
        if (!cancelled) { setItems(rows); setTotal(t); }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load gallery');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [selectedFormat, selectedIndustry, selectedComponent, debouncedSearch]);

  const hasMore = items.length < total;

  // Append the next page when the sentinel scrolls into view.
  const loadMore = useCallback(async () => {
    if (loadingMore || loading || !hasMore) return;
    setLoadingMore(true);
    try {
      const { items: rows } = await fetchGalleryItemsFiltered({
        format: selectedFormat,
        industry: selectedIndustry,
        component: selectedComponent,
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset: items.length,
      });
      setItems(prev => {
        const seen = new Set(prev.map(i => i.id));
        return [...prev, ...rows.filter(r => !seen.has(r.id))];
      });
    } catch { /* keep what we already have on a failed page load */ }
    finally { setLoadingMore(false); }
  }, [loadingMore, loading, hasMore, selectedFormat, selectedIndustry, selectedComponent, debouncedSearch, items.length]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: '200px 0px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  // The server already filtered/paginated; these keep the JSX below unchanged.
  const filteredItems = items;
  const visibleItems = items;
  const showFeatured = !hasActiveFilters;
  const featuredItems = showFeatured ? featured.slice(0, FEATURED_COUNT) : [];
  const featuredIds = new Set(featuredItems.map(i => i.id));
  const gridItems = showFeatured ? items.filter(i => !featuredIds.has(i.id)) : items;

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedFormat('all');
    setSelectedIndustry('all');
    setSelectedComponent('all');
    // Preserve mode in the URL, clear everything else
    setSearchParams(prev => {
      const next = new URLSearchParams();
      if (prev.has('mode')) next.set('mode', prev.get('mode')!);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const handleViewDemo = useCallback((item: GalleryItem) => {
    const url = `/gallery/preview?item=${encodeURIComponent(item.slug)}&fullscreen=true`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const handleImport = useCallback((item: GalleryItem) => {
    if (item.console_import_url) window.open(item.console_import_url, '_blank', 'noopener,noreferrer');
  }, []);

  // Select styles scoped to theme
  const selectClass = `w-full pl-3 pr-8 py-2 text-sm rounded-lg border appearance-none bg-[length:14px_14px] bg-[right_10px_center] bg-no-repeat focus:outline-none focus:ring-2 focus:ring-[#635bff]/50
    ${mode === 'dark'
      ? 'bg-white/5 border-white/10 text-white bg-[url("data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A//www.w3.org/2000/svg%27%20fill%3D%27none%27%20viewBox%3D%270%200%2024%2024%27%20stroke%3D%27%2394a3b8%27%20stroke-width%3D%272%27%3E%3Cpath%20d%3D%27M19%209l-7%207-7-7%27/%3E%3C/svg%3E")]'
      : 'bg-slate-50 border-slate-200 text-slate-900 bg-[url("data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A//www.w3.org/2000/svg%27%20fill%3D%27none%27%20viewBox%3D%270%200%2024%2024%27%20stroke%3D%27%2394a3b8%27%20stroke-width%3D%272%27%3E%3Cpath%20d%3D%27M19%209l-7%207-7-7%27/%3E%3C/svg%3E")]'
    }`;

  const isDark = mode === 'dark';

  return (
    <div className={mode}>
      <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0f] text-white' : 'bg-white text-slate-900'}`}>

        {/* Filter bar */}
        <div
          ref={null}
          className={`sticky top-0 z-10 px-4 py-3 border-b backdrop-blur-sm ${isDark ? 'border-white/10 bg-[#0a0a0f]/95' : 'border-slate-200 bg-white/95'}`}
        >
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search gallery..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className={`w-full pl-9 pr-4 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#635bff]/50
                  ${mode === 'dark'
                    ? 'bg-white/5 border-white/10 text-white placeholder-slate-400'
                    : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
                  }`}
              />
            </div>

            {/* Dropdowns */}
            <div className="flex gap-2 flex-shrink-0">
              <select value={selectedFormat} onChange={e => setSelectedFormat(e.target.value)} className={selectClass}>
                {formatOptions.map(opt => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
              </select>
              <select value={selectedIndustry} onChange={e => setSelectedIndustry(e.target.value)} className={selectClass}>
                {industryOptions.map(opt => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
              </select>
              <select value={selectedComponent} onChange={e => setSelectedComponent(e.target.value)} className={selectClass}>
                {componentOptions.map(opt => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
              </select>
            </div>

            {/* Count + clear */}
            <div className={`flex items-center gap-2 flex-shrink-0 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <span>{items.length}{total > items.length ? ` of ${total}` : ''} results</span>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1 text-[#635bff] hover:text-[#5449e6] transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 py-4">
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-[#635bff]" />
            </div>
          )}

          {error && !loading && (
            <div className="flex items-center justify-center py-20 text-sm text-red-500">
              {error}
            </div>
          )}

          {!loading && !error && filteredItems.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>No results found.</p>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-sm text-[#635bff] hover:underline">
                  Clear filters
                </button>
              )}
            </div>
          )}

          {!loading && !error && visibleItems.length > 0 && (
            <>
              {/* Featured row */}
              {showFeatured && featuredItems.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                  {featuredItems.map((item, idx) => (
                    <GalleryCard
                      key={item.id}
                      item={item}
                      idx={idx}
                      isDark={isDark}
                      featured
                      onViewDemo={handleViewDemo}
                      onImport={handleImport}
                    />
                  ))}
                </div>
              )}

              {/* Regular grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {gridItems.map((item, idx) => (
                  <GalleryCard
                    key={item.id}
                    item={item}
                    idx={showFeatured ? idx + FEATURED_COUNT : idx}
                    isDark={isDark}
                    onViewDemo={handleViewDemo}
                    onImport={handleImport}
                  />
                ))}
              </div>

              {/* Infinite scroll sentinel */}
              <div ref={sentinelRef} className="py-6 flex items-center justify-center">
                {hasMore && <Loader2 className="w-5 h-5 animate-spin text-[#635bff]" />}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InAppGallery;
